import { WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import {
  type CancelShiftPolicy,
  type ClipboardScoreProfileResponse,
} from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { differenceInMinutes, isBefore, parseISO, subHours } from "date-fns";

interface CancellationPenaltyPreviewResult {
  /**
   * Whether the penalty preview should be visible.
   * False when there are no cancel shift policies.
   */
  isVisible: boolean;

  /**
   * Points the worker would lose if they cancelled right now.
   * Already EC-adjusted when isUsingEmergencyCredit is true.
   * Undefined if cancelling now results in a restriction.
   */
  cancelNowPoints: number | undefined;

  /**
   * Whether cancelling now would result in an account restriction.
   */
  cancelNowIsRestriction: boolean;

  /**
   * Whether an Emergency Credit will be consumed by cancelling now.
   * True only when EC is enabled, credits are available, and the current bracket
   * defines a pointsWithEmergencyCredit value.
   */
  isUsingEmergencyCredit: boolean;

  /**
   * The date/time when the penalty is expected to increase,
   * or undefined if we are in the last bracket (next increase is after shift start).
   */
  nextIncreaseDate: Date | undefined;

  /**
   * Points at the next lead time bracket.
   * Already EC-adjusted when isUsingEmergencyCredit is true and the bracket defines
   * a pointsWithEmergencyCredit value.
   * Undefined if the next bracket results in a restriction.
   */
  nextBracketPoints: number | undefined;

  /**
   * Whether the next bracket would result in an account restriction
   * (i.e., the current bracket is the last one before shift start).
   */
  nextBracketIsRestriction: boolean;
}

/**
 * Finds the cancel shift bracket matching the given lead time,
 * and the next (more penalizing) bracket after it.
 * Sorts the policies once — longest to shortest lead time — to avoid redundant work.
 */
function findCurrentAndNextBracket(
  cancelShiftPolicies: CancelShiftPolicy[],
  leadTimeHours: number
): {
  current: CancelShiftPolicy | undefined;
  next: CancelShiftPolicy | undefined;
} {
  const sorted = [...cancelShiftPolicies].sort((a, b) => {
    const aMin = a.leadTime[0] ?? 0;
    const bMin = b.leadTime[0] ?? 0;
    return bMin - aMin;
  });

  const currentIndex = sorted.findIndex((policy) => {
    const [minHours, maxHours] = policy.leadTime;
    const meetsMinimum = !isDefined(minHours) || leadTimeHours >= minHours;
    const meetsMaximum = !isDefined(maxHours) || leadTimeHours < maxHours;
    return meetsMinimum && meetsMaximum;
  });

  if (currentIndex < 0) {
    return { current: undefined, next: undefined };
  }

  return {
    current: sorted[currentIndex],
    next: currentIndex < sorted.length - 1 ? sorted[currentIndex + 1] : undefined,
  };
}

export function computeCancellationPenaltyPreview(params: {
  clipboardScoreProfile: ClipboardScoreProfileResponse;
  shiftStart: string;
  currentDate: Date;
  emergencyCredits?: { enabled: boolean; availableCredits: number };
}): CancellationPenaltyPreviewResult {
  const { clipboardScoreProfile, shiftStart, currentDate, emergencyCredits } = params;

  const isRestricted = clipboardScoreProfile.accountStatus?.status === WorkerStage.RESTRICTED;
  const cancelShiftPolicies = clipboardScoreProfile.policy?.cancelShift;
  if (isRestricted || !isDefined(cancelShiftPolicies) || cancelShiftPolicies.length === 0) {
    return {
      isVisible: false,
      cancelNowPoints: undefined,
      cancelNowIsRestriction: false,
      isUsingEmergencyCredit: false,
      nextIncreaseDate: undefined,
      nextBracketPoints: undefined,
      nextBracketIsRestriction: false,
    };
  }

  const useEmergencyCredit =
    (emergencyCredits?.enabled ?? false) && (emergencyCredits?.availableCredits ?? 0) > 0;

  const shiftStartDate = parseISO(shiftStart);

  // If the shift has already started, cancelling now is a restriction.
  // Compare dates directly to avoid truncation errors from differenceInMinutes.
  const isAfterShiftStart = !isBefore(currentDate, shiftStartDate);

  const leadTimeHours = differenceInMinutes(shiftStartDate, currentDate) / 60;
  const { current: currentBracket, next: nextBracket } = isAfterShiftStart
    ? { current: undefined, next: undefined }
    : findCurrentAndNextBracket(cancelShiftPolicies, leadTimeHours);

  // Determine "Cancel now" row
  let cancelNowPoints: number | undefined;
  let cancelNowIsRestriction = false;
  let isUsingEmergencyCredit = false;

  if (isAfterShiftStart || !isDefined(currentBracket)) {
    cancelNowIsRestriction = true;
  } else {
    const wouldResultInRestriction =
      clipboardScoreProfile.clipboardScore + currentBracket.points <= 0;
    if (wouldResultInRestriction) {
      cancelNowIsRestriction = true;
    } else if (useEmergencyCredit && isDefined(currentBracket.pointsWithEmergencyCredit)) {
      cancelNowPoints = currentBracket.pointsWithEmergencyCredit;
      isUsingEmergencyCredit = true;
    } else {
      cancelNowPoints = currentBracket.points;
    }
  }

  // Determine "Cancel after..." row
  let nextIncreaseDate: Date | undefined;
  let nextBracketPoints: number | undefined;
  let nextBracketIsRestriction = false;

  if (cancelNowIsRestriction) {
    nextBracketIsRestriction = true;
  } else {
    // cancelNowIsRestriction is false, so currentBracket is defined
    const [currentMinHours] = currentBracket!.leadTime;

    if (!isDefined(currentMinHours) || currentMinHours === 0) {
      // This is the last bracket (e.g., 0-8h). Next increase is after shift start.
      nextBracketIsRestriction = true;
    } else if (!isDefined(nextBracket)) {
      // No bracket with lower lead time; next is after shift start
      nextBracketIsRestriction = true;
    } else {
      nextIncreaseDate = subHours(shiftStartDate, currentMinHours);
      if (clipboardScoreProfile.clipboardScore + nextBracket.points <= 0) {
        nextBracketIsRestriction = true;
      } else if (useEmergencyCredit && isDefined(nextBracket.pointsWithEmergencyCredit)) {
        nextBracketPoints = nextBracket.pointsWithEmergencyCredit;
      } else {
        nextBracketPoints = nextBracket.points;
      }
    }
  }

  return {
    isVisible: true,
    cancelNowPoints,
    cancelNowIsRestriction,
    isUsingEmergencyCredit,
    nextIncreaseDate,
    nextBracketPoints,
    nextBracketIsRestriction,
  };
}
